import { Development } from 'types'
import { mediacoreApi } from '../mediacoreApi'
interface QueryParams {
  CLIENT_ID?: string | number
  params_get?: Record<string, any> | string
}

interface DetailQueryParams extends QueryParams {
  DEVELOPMENT_ID?: string | number
}

interface PropertyDetailQueryParams extends QueryParams {
  PROPERTY_ID?: string | number
}

interface XintelLocationSuggestion {
  id: string
  idU: string
  descripcion: string
}

const mapXintelOperationValue = (operation: string) => {
  const normalized = operation.trim().toLowerCase()
  if (normalized === 'venta' || normalized === 'v') {
    return 'V'
  }
  if (normalized === 'alquiler' || normalized === 'a') {
    return 'A'
  }
  if (
    normalized === 'alquiler-temporario' ||
    normalized === 'alquiler temporario' ||
    normalized === 'temporario' ||
    normalized === 't'
  ) {
    return 'T'
  }
  return operation
}

const normalizeXintelOperationParams = (params: URLSearchParams) => {
  const values = params.getAll('operation_type')
  if (values.length === 0) {
    return params
  }
  params.delete('operation_type')
  values.forEach((value) => params.append('operation_type', mapXintelOperationValue(value)))
  return params
}

export const xintelDevelopmentsExtended = mediacoreApi.injectEndpoints({
  endpoints: (builder) => ({
    getLocationSuggestionsXintel: builder.query<
      XintelLocationSuggestion[],
      { CLIENT_ID?: string | number; like: string }
    >({
      query: ({ CLIENT_ID, like }) => {
        const client = CLIENT_ID ?? process.env.GATSBY_CLIENTID
        const params = new URLSearchParams({
          client: String(client),
          like,
        })
        return `integrations/xintel/ubicaciones/?${params.toString()}`
      },
    }),
    getDevelopmentsXintel: builder.query<Development[], QueryParams | void>({
      query: ({ CLIENT_ID } = {}) => {
        const client = CLIENT_ID ?? process.env.GATSBY_CLIENTID
        return `integrations/xintel/emprendimientos/?client=${client}`
      },
    }),
    getPropertiesXintel: builder.query<
      { meta: { total_count: number }; objects: any[] },
      QueryParams | void
    >({
      query: ({ CLIENT_ID, params_get } = {}) => {
        const client = CLIENT_ID ?? process.env.GATSBY_CLIENTID
        const qs = params_get
          ? normalizeXintelOperationParams(new URLSearchParams(params_get)).toString()
          : ''
        return `integrations/xintel/propiedades/?client=${client}${qs ? `&${qs}` : ''}`
      },
    }),
    getSummaryXintel: builder.query({
      query: ({ CLIENT_ID, OPERATION, TYPE }) => {
        const operationType = OPERATION ? mapXintelOperationValue(OPERATION) : ''
        return `integrations/xintel/summary/?client=${CLIENT_ID}${`&operation_type=${operationType}`}${
          TYPE?.length
            ? '&property_type=' + TYPE?.map((element: { name: string; id: string }) => element.name)
            : ''
        }`
      },
    }),
    getDevelopmentXintel: builder.query<Development, DetailQueryParams>({
      query: ({ CLIENT_ID, DEVELOPMENT_ID }) => {
        const client = CLIENT_ID ?? process.env.GATSBY_CLIENTID
        const id = DEVELOPMENT_ID ?? ''
        return `integrations/xintel/emprendimientos/${id}/?client=${client}`
      },
    }),
    getDevelopmentUnitsXintel: builder.query({
      query: ({ CLIENT_ID, DEVELOPMENT_ID }) => {
        const client = CLIENT_ID ?? process.env.GATSBY_CLIENTID
        const id = DEVELOPMENT_ID ?? ''
        const params = new URLSearchParams({
          client: String(client),
          with_units: 'true',
        })
        return `integrations/xintel/emprendimientos/${id}/?${params.toString()}`
      },
    }),
    getPropertyXintel: builder.query({
      query: ({ CLIENT_ID, PROPERTY_ID }: PropertyDetailQueryParams) => {
        const client = CLIENT_ID ?? process.env.GATSBY_CLIENTID
        const id = PROPERTY_ID ?? ''
        const params = new URLSearchParams({
          client: String(client),
        })
        return `integrations/xintel/propiedades/${id}/?${params.toString()}`
      },
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetLocationSuggestionsXintelQuery,
  useGetDevelopmentsXintelQuery,
  useGetPropertiesXintelQuery,
  useGetSummaryXintelQuery,
  useGetDevelopmentXintelQuery,
  useGetDevelopmentUnitsXintelQuery,
  useGetPropertyXintelQuery,
} = xintelDevelopmentsExtended
